import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  UserCheck, 
  UserX, 
  Eye, 
  Download, 
  Search, 
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  GraduationCap,
  MapPin,
  Phone,
  Mail,
  FileText,
  Star,
  Users,
  BookOpen,
  Calendar
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { tuitionTeacherService } from '../../firebase/tuition';
import { saveMasterTimetable, getMasterTimetable } from '../../firebase/collections';
import toast from 'react-hot-toast';
// import MasterTimetableManagement from './MasterTimetableManagement';

const TuitionTeacherManagement = () => {
  const { adminData, logAdminAction } = useRealtime();
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [periods, setPeriods] = useState([
    'Period 1 (9:00-9:45)',
    'Period 2 (9:45-10:30)',
    'Period 3 (10:30-11:15)',
    'Period 4 (11:15-12:00)',
    'Break (12:00-12:30)',
    'Period 5 (12:30-1:15)',
    'Period 6 (1:15-2:00)',
    'Period 7 (2:00-2:45)',
    'Period 8 (2:45-3:30)'
  ]);
  const [subjects, setSubjects] = useState([]);
  const [newSubjectInput, setNewSubjectInput] = useState('');
  const [isSavingTimetableConfig, setIsSavingTimetableConfig] = useState(false);

  // Load teachers
  useEffect(() => {
    loadTeachers();
    loadTimetableConfig();
  }, []);

  const coachingId = adminData?.coachingId || null;

  const loadTimetableConfig = async () => {
    try {
      const timetableResult = await getMasterTimetable(coachingId);
      if (timetableResult.success) {
        const timetableData = timetableResult.data || {};
        const loadedPeriods = timetableData.periods || periods;
        const loadedSubjects = timetableData.subjects || [];

        setPeriods(loadedPeriods);
        setSubjects(loadedSubjects.length ? loadedSubjects : subjects);
      }
    } catch (error) {
      console.error('Failed to load timetable config', error);
    }
  };

  const loadTeachers = async () => {
    try {
      setLoading(true);
      // Get all teachers regardless of status for admin view
      const allTeachers = await tuitionTeacherService.getAllTeachers();
      setTeachers(allTeachers);
    } catch (error) {
      toast.error('Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTeacher = async (teacherId) => {
    if (!window.confirm('Are you sure you want to approve this teacher?')) return;
    
    setIsProcessing(true);
    try {
      await tuitionTeacherService.approveTeacher(teacherId);
      await logAdminAction('tuition_teacher_approved', { teacherId });
      toast.success('Teacher approved successfully');
      loadTeachers();
      setShowDetailsModal(false);
    } catch (error) {
      toast.error('Failed to approve teacher');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectTeacher = async (teacherId) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (!reason) return;
    
    setIsProcessing(true);
    try {
      await tuitionTeacherService.rejectTeacher(teacherId, reason);
      await logAdminAction('tuition_teacher_rejected', { teacherId, reason });
      toast.success('Teacher rejected successfully');
      loadTeachers();
      setShowDetailsModal(false);
    } catch (error) {
      toast.error('Failed to reject teacher');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewDetails = (teacher) => {
    setSelectedTeacher(teacher);
    setShowDetailsModal(true);
  };

  const handleRecalculateAllStudentCounts = async () => {
    if (!window.confirm('This will recalculate totalStudents count for all teachers. Continue?')) {
      return;
    }

    setIsRecalculating(true);
    try {
      const allTeachers = await tuitionTeacherService.getAllTeachers();
      let successCount = 0;
      let errorCount = 0;

      for (const teacher of allTeachers) {
        try {
          await tuitionTeacherService.recalculateTotalStudents(teacher.id);
          successCount++;
        } catch (error) {
          console.error(`Failed to recalculate for teacher ${teacher.id}:`, error);
          errorCount++;
        }
      }

      await logAdminAction('recalculate_all_student_counts', { 
        total: allTeachers.length, 
        success: successCount, 
        errors: errorCount 
      });

      toast.success(`Recalculation complete! ${successCount} teachers updated${errorCount > 0 ? `, ${errorCount} errors` : ''}`);
      loadTeachers(); // Refresh the list
    } catch (error) {
      toast.error('Failed to recalculate student counts');
      console.error(error);
    } finally {
      setIsRecalculating(false);
    }
  };

  const saveTimetableConfig = async () => {
    setIsSavingTimetableConfig(true);
    try {
      const result = await saveMasterTimetable([], coachingId, { periods, subjects });
      if (result.success) {
        toast.success('Timetable configuration saved');
      } else {
        toast.error('Failed to save timetable configuration');
      }
    } catch (error) {
      toast.error('Failed to save timetable config');
      console.error(error);
    } finally {
      setIsSavingTimetableConfig(false);
    }
  };

  const handleAddSubject = () => {
    const trimmed = newSubjectInput.trim();
    if (!trimmed) {
      toast.error('Please enter a valid subject name');
      return;
    }
    if (subjects.includes(trimmed)) {
      toast.error('Subject already exists');
      return;
    }
    setSubjects((prev) => [...prev, trimmed].sort());
    setNewSubjectInput('');
  };

  const handleRemoveSubject = (subject) => {
    setSubjects((prev) => prev.filter((s) => s !== subject));
  };

  const handleAddPeriod = () => {
    setPeriods((prev) => [...prev, `Period ${prev.length + 1} (start-end)`]);
  };

  const handleRemovePeriod = (idx) => {
    setPeriods((prev) => prev.filter((_, i) => i !== idx));
  };

  const handlePeriodLabelChange = (idx, value) => {
    setPeriods((prev) => prev.map((p, i) => i === idx ? value : p));
  };

  const filteredTeachers = teachers.filter(teacher => {
    const matchesSearch = 
      teacher.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      teacher.phone?.includes(searchTerm) ||
      teacher.subjects?.some(subject => subject.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || teacher.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const stats = {
    total: teachers.length,
    pending: teachers.filter(t => t.status === 'pending').length,
    approved: teachers.filter(t => t.status === 'approved').length,
    rejected: teachers.filter(t => t.status === 'rejected').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Tuition Teacher Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Review and approve tuition teacher applications
            </p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={saveTimetableConfig}
              disabled={isSavingTimetableConfig}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
            >
              <Calendar className="w-4 h-4" />
              <span>{isSavingTimetableConfig ? 'Saving Config...' : 'Save Timetable Config'}</span>
            </button>
            <button
              onClick={handleRecalculateAllStudentCounts}
              disabled={isRecalculating}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isRecalculating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Recalculating...</span>
                </>
              ) : (
                <>
                  <Users className="w-4 h-4" />
                  <span>Recalculate Student Counts</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Timetable Configuration */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Period Timings</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {periods.map((period, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      value={period}
                      onChange={(e) => handlePeriodLabelChange(idx, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      onClick={() => handleRemovePeriod(idx)}
                      className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={handleAddPeriod}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Period
                </button>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-3">Subjects (Editable)</h3>
              <div className="flex gap-2 mb-3">
                <input
                  value={newSubjectInput}
                  onChange={(e) => setNewSubjectInput(e.target.value)}
                  placeholder="Add new subject"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <button
                  onClick={handleAddSubject}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Add
                </button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {subjects.length === 0 ? (
                  <p className="text-gray-500">No subjects configured yet.</p>
                ) : (
                  subjects.map((subj) => (
                    <div key={subj} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-md px-3 py-2">
                      <span className="text-sm text-gray-800 dark:text-gray-100">{subj}</span>
                      <button
                        onClick={() => handleRemoveSubject(subj)}
                        className="text-red-600 dark:text-red-300 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Teachers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.pending}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Approved</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.approved}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Rejected</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.rejected}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search teachers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Teachers Table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Teacher
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Subjects
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Fees
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Applied
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTeachers.map((teacher) => (
                  <motion.tr
                    key={teacher.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {teacher.photoUrl ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={teacher.photoUrl}
                              alt={teacher.name}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                              <GraduationCap className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {teacher.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {teacher.qualification}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{teacher.email}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{teacher.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {teacher.subjects?.slice(0, 2).join(', ')}
                        {teacher.subjects?.length > 2 && ` +${teacher.subjects.length - 2} more`}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        ₹{teacher.feesPerHour}/hour
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(teacher.status)}`}>
                        {getStatusIcon(teacher.status)}
                        <span className="ml-1 capitalize">{teacher.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {teacher.createdAt ? new Date(teacher.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(teacher)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {teacher.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApproveTeacher(teacher.id)}
                              disabled={isProcessing}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRejectTeacher(teacher.id)}
                              disabled={isProcessing}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                            >
                              <UserX className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredTeachers.length === 0 && (
          <div className="text-center py-12">
            <GraduationCap className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No teachers found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'No tuition teachers have registered yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Teacher Details Modal */}
      {showDetailsModal && selectedTeacher && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Teacher Details
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
                  
                  <div className="flex items-center space-x-4">
                    {selectedTeacher.photoUrl ? (
                      <img
                        className="h-20 w-20 rounded-full object-cover"
                        src={selectedTeacher.photoUrl}
                        alt={selectedTeacher.name}
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                        <GraduationCap className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                      </div>
                    )}
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {selectedTeacher.name}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400">
                        {selectedTeacher.qualification}
                      </p>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTeacher.status)}`}>
                        {getStatusIcon(selectedTeacher.status)}
                        <span className="ml-1 capitalize">{selectedTeacher.status}</span>
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{selectedTeacher.email}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900 dark:text-white">{selectedTeacher.phone}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-900 dark:text-white">
                        {selectedTeacher.locality}, {selectedTeacher.city}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-400 font-semibold">₹</span>
                      <span className="text-gray-900 dark:text-white">
                        {selectedTeacher.feesPerHour}/hour
                      </span>
                    </div>
                  </div>
                </div>

                {/* Teaching Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Teaching Details</h3>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Subjects</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTeacher.subjects?.map((subject, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 rounded text-sm"
                        >
                          {subject}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Classes</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTeacher.classes?.map((cls, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 rounded text-sm"
                        >
                          {cls}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Availability</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTeacher.availability?.map((time, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 rounded text-sm"
                        >
                          {time}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">Experience</h4>
                    <p className="text-gray-600 dark:text-gray-400">{selectedTeacher.experience} years</p>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {selectedTeacher.bio && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Bio</h3>
                  <p className="text-gray-600 dark:text-gray-400">{selectedTeacher.bio}</p>
                </div>
              )}

              {/* Teaching Method */}
              {selectedTeacher.teachingMethod && (
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Teaching Method</h3>
                  <p className="text-gray-600 dark:text-gray-400">{selectedTeacher.teachingMethod}</p>
                </div>
              )}

              {/* Documents */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedTeacher.aadharUrl && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-900 dark:text-white">Aadhar Card</span>
                        </div>
                        <a
                          href={selectedTeacher.aadharUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {selectedTeacher.panUrl && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-5 h-5 text-gray-400" />
                          <span className="text-gray-900 dark:text-white">PAN Card</span>
                        </div>
                        <a
                          href={selectedTeacher.panUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              {selectedTeacher.status === 'pending' && (
                <div className="mt-8 flex justify-end space-x-4">
                  <button
                    onClick={() => handleRejectTeacher(selectedTeacher.id)}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <UserX className="w-4 h-4" />
                    <span>Reject</span>
                  </button>
                  <button
                    onClick={() => handleApproveTeacher(selectedTeacher.id)}
                    disabled={isProcessing}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <UserCheck className="w-4 h-4" />
                    <span>Approve</span>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Timetable Management is now in-page configurable via the section above */}
    </div>
  );
};

export default TuitionTeacherManagement;
